import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type {
  NavigationConfig,
  NavSectionConfig,
  NavItemConfig,
  ProcessedNavSection,
  ProcessedNavItem,
  UserRole,
} from './types';
import { getIcon } from './icons';

/**
 * Check if a user role can access a config item/section
 * @param userRole - Current user's role (or null if not authenticated)
 * @param allowedRoles - Array of roles that can access this item
 * @returns true if user has access
 */
function hasRoleAccess(userRole: UserRole | null, allowedRoles: UserRole[]): boolean {
  // If no user, no access
  if (!userRole) return false;

  // Empty roles array means visible to all authenticated users
  if (allowedRoles.length === 0) return true;

  // SiteAdmin sees everything (inherits all access)
  if (userRole === 'SiteAdmin') return true;

  // Check if user's role is in allowed roles
  return allowedRoles.includes(userRole);
}

/**
 * Filter and sort navigation items by role
 */
function processItems(items: NavItemConfig[], userRole: UserRole | null): ProcessedNavItem[] {
  return items
    .filter((item) => hasRoleAccess(userRole, item.roles))
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      id: item.id,
      label: item.label,
      href: item.href,
      icon: getIcon(item.icon),
    }));
}

/**
 * Filter and sort navigation sections by role
 */
function processSections(
  sections: NavSectionConfig[],
  userRole: UserRole | null
): ProcessedNavSection[] {
  return sections
    .filter((section) => hasRoleAccess(userRole, section.roles))
    .sort((a, b) => a.order - b.order)
    .map((section) => {
      const processedItems = processItems(section.items, userRole);
      return {
        id: section.id,
        label: section.label,
        items: processedItems,
        divider: section.divider,
      };
    })
    // Remove sections with no visible items
    .filter((section) => section.items.length > 0);
}

/**
 * Hook to get role-filtered navigation
 *
 * @param config - The navigation configuration
 * @returns Processed navigation sections filtered by current user's role
 *
 * @example
 * const { sections } = useNavigation(navigationConfig);
 * // Returns only sections/items the current user can see
 */
export function useNavigation(config: NavigationConfig): {
  sections: ProcessedNavSection[];
  isLoading: boolean;
} {
  const { user, isLoading } = useAuth();

  const sections = useMemo(() => {
    if (isLoading || !user) {
      return [];
    }
    return processSections(config.sections, user.role);
  }, [config.sections, user, isLoading]);

  return { sections, isLoading };
}

